import { CommonOperations, contains } from "../../../store/permissions";
import { connect } from "react-redux";
import { IsEnterpriseTimeTracker } from "../../../store/TimeTrackingStore";
import { ApplicationState } from "../../../store";
import { actionCreators as tenantActionCreators, Subscription, PPMFeatures } from "../../../store/Tenant";
import { bindActionCreators, Dispatch } from "redux";
import "./timeTrackingSettings.css";
import TimeReportingSettingsTooltip from "../../common/sectionsControl/uiControls/timeReportingSettingsControl/TimeReportingSettingsTooltip";
import SectionableSettingsPage from "../SectionableSettingsPage";
import ReportedTimeSynchronizationSettingsTooltip from "../../common/sectionsControl/uiControls/reportedTimeSynchronizationSettingsControl/ReportedTimeSynchronizationSettingsTooltip";
import TimeTrackingBillableSettingsTooltip from "../../common/sectionsControl/uiControls/timeTrackingBillableSettingsControl/TimeTrackingBillableSettingsTooltip";
import TimeTrackingAdministrativeTooltip from "../../common/sectionsControl/uiControls/timeTrackingAdministrativeControl/TimeTrackingAdministrativeTooltip";
import { IsTimesheetApprovalsEnabled } from "../../timeTracking/common";
import { Section } from "../../../entities/Metadata";
import TimesheetApprovalEmailRemindersTooltip from "../../common/sectionsControl/uiControls/timesheetApprovalEmailRemindersControl/TimesheetApprovalEmailRemindersTooltip";
import TimesheetApprovalsTooltip from "../../common/sectionsControl/uiControls/timesheetApprovalsControl/TimesheetApprovalsTooltip";

export const TimeTrackingSettingsSectionKeys = {
    globalSettings: "globalSettings",
    timesheetApprovals: "timesheetApprovals",
    timesheetApprovalEmailReminders: "timesheetApprovalEmailReminders",
    reportedTimeSync: "reportedTimeSync",
    billableCostTypeSettings: "billableCostTypeSettings",
    reportingControls: "reportingControls",
    administrativeCategories: "administrativeCategories"
} as const;

type TimeTrackingSettingsSectionKeys =
    (typeof TimeTrackingSettingsSectionKeys)[keyof typeof TimeTrackingSettingsSectionKeys];

const timeTrackingSettingsSectionMap: Record<
    TimeTrackingSettingsSectionKeys,
    { name: string; iconName: string; tooltip: JSX.Element | undefined; controlName: string; navigationLabel?: string }
> = {
    [TimeTrackingSettingsSectionKeys.globalSettings]: {
        name: "Time Reporting Settings",
        navigationLabel: "Reporting Settings",
        iconName: "Settings",
        tooltip: <TimeReportingSettingsTooltip />,
        controlName: "TimeReportingSettingsControl"
    },
    [TimeTrackingSettingsSectionKeys.timesheetApprovals]: {
        name: "Timesheet Approvals",
        navigationLabel: "Timesheet Approvals",
        iconName: "CheckMark",
        tooltip: <TimesheetApprovalsTooltip />,
        controlName: "TimesheetApprovalsControl"
    },
    [TimeTrackingSettingsSectionKeys.reportedTimeSync]: {
        name: "Reported Time synchronization",
        navigationLabel: "Reported Time sync",
        iconName: "ReminderTime",
        tooltip: <ReportedTimeSynchronizationSettingsTooltip />,
        controlName: "ReportedTimeSynchronizationSettingsControl"
    },
    [TimeTrackingSettingsSectionKeys.billableCostTypeSettings]: {
        name: "Billable and Cost Type synchronization",
        navigationLabel: "Billable & Cost Type",
        iconName: "Money",
        tooltip: <TimeTrackingBillableSettingsTooltip />,
        controlName: "TimeTrackingBillableSettingsControl"
    },
    [TimeTrackingSettingsSectionKeys.reportingControls]: {
        name: "Time Reporting controls",
        navigationLabel: "Reporting controls",
        iconName: "TimeEntry",
        tooltip: undefined,
        controlName: "TimeReportingControlsControl"
    },
    [TimeTrackingSettingsSectionKeys.administrativeCategories]: {
        name: "Administrative Categories",
        navigationLabel: "Admin Categories",
        iconName: "BulletedList2",
        tooltip: <TimeTrackingAdministrativeTooltip />,
        controlName: "TimeTrackingAdministrativeControl"
    },
    [TimeTrackingSettingsSectionKeys.timesheetApprovalEmailReminders]: {
        name: "Timesheet Approval Email Reminders",
        navigationLabel: "Approval Reminders",
        iconName: "Mail",
        tooltip: <TimesheetApprovalEmailRemindersTooltip />,
        controlName: "TimesheetApprovalEmailRemindersControl"
    }
};

const buildSectionSettings = (
    key: TimeTrackingSettingsSectionKeys,
    isSelected = true,
    isOpen = true,
    isAccessible = true,
    uiControlsSections = []
): Section => ({
    id: key,
    name: timeTrackingSettingsSectionMap[key].name,
    navigationLabel: timeTrackingSettingsSectionMap[key].navigationLabel,
    settings: {
        iconName: timeTrackingSettingsSectionMap[key].iconName,
        tooltip: timeTrackingSettingsSectionMap[key].tooltip
    },
    isSelected: isSelected,
    isOpen: isOpen,
    isAccessible: isAccessible,
    uiControls: [
        {
            id: key,
            type: timeTrackingSettingsSectionMap[key].controlName,
            settings: uiControlsSections
        }
    ]
});

type StateProps = {
    isEnterpriseTimeTracker: boolean;
    hasTimesheetApprovalsFeature: boolean;
    isTimesheetApprovalsEnabled: boolean;
    canManage: boolean;
};

type ActionProps = {
    tenantActions: typeof tenantActionCreators;
};

type Props = StateProps & ActionProps;

function TimeTrackingSettings(props: Props) {
    const { isEnterpriseTimeTracker } = props;

    const isTimesheetApprovalsEnabled = props.hasTimesheetApprovalsFeature && props.isTimesheetApprovalsEnabled;

    const sections: Section[] = [
        buildSectionSettings(TimeTrackingSettingsSectionKeys.globalSettings),
        buildSectionSettings(TimeTrackingSettingsSectionKeys.timesheetApprovals),
        buildSectionSettings(TimeTrackingSettingsSectionKeys.reportedTimeSync),
        buildSectionSettings(TimeTrackingSettingsSectionKeys.billableCostTypeSettings, isEnterpriseTimeTracker),
        buildSectionSettings(TimeTrackingSettingsSectionKeys.reportingControls),
        buildSectionSettings(TimeTrackingSettingsSectionKeys.administrativeCategories),
        buildSectionSettings(TimeTrackingSettingsSectionKeys.timesheetApprovalEmailReminders, isTimesheetApprovalsEnabled)
    ];

    return (
        <SectionableSettingsPage
            title="Time Tracking Settings"
            requiredPermission={CommonOperations.TimeTrackingView}
            sections={sections}
            settingsKey="timeTrackingSettings"
        />
    );
}

const mapStateToProps = (_: ApplicationState): StateProps => ({
    isEnterpriseTimeTracker: IsEnterpriseTimeTracker(_.tenant.subscription),
    hasTimesheetApprovalsFeature: Subscription.contains(_.tenant.subscription, PPMFeatures.TimesheetApprovals),
    isTimesheetApprovalsEnabled: IsTimesheetApprovalsEnabled(_.tenant),
    canManage: contains(_.user.permissions.common, CommonOperations.TimeTrackingManage)
});

const mapDispatchToProps = (dispatch: Dispatch): ActionProps => {
    return {
        tenantActions: bindActionCreators(tenantActionCreators, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TimeTrackingSettings);
